/**
 * `/api/work-status` — реестр и панель статусов работ (задача 114 §1.2/§1.3).
 *
 * Работа = пара «проект × вид работ», приходящая ИЗ ТРЕКИНГА: реестр наполняется
 * тем, на что люди отчитались, а не отдельным списком задач. Overlay
 * (`project_works`) ленивый — у работы без него сервер отдаёт дефолтный статус
 * «в работе» и превью контрольных точек из каталога, а строка заводится первой
 * же мутацией руководителя.
 */
import { api } from "./client";
import type { components } from "./types";

export type WorkStatusRegistry = components["schemas"]["WorkStatusRegistry"];
export type RegistryProject = components["schemas"]["RegistryProject"];
export type RegistryWork = components["schemas"]["RegistryWork"];
export type RegistryCheckpoint = components["schemas"]["RegistryCheckpoint"];
export type WorkStatusDetail = components["schemas"]["WorkStatusDetail"];
export type WorkCheckpoint = components["schemas"]["WorkCheckpoint"];
export type WorkEventView = components["schemas"]["WorkEventView"];
export type ProjectWorkStatus = components["schemas"]["ProjectWorkStatus"];
export type WorkForecast = components["schemas"]["WorkForecast"];

/**
 * Реестр сцены актора. `unitId` обязателен для admin (у него нет
 * acting-поверхности — бэк ответит 422 без него), для лида/зама опционален:
 * без него сервер отдаёт всё поддерево ведомых.
 */
function sceneQuery(unitId: number | null | undefined, includeChildren: boolean): string {
  const params = new URLSearchParams();
  if (unitId != null) params.set("unit_id", String(unitId));
  if (includeChildren) params.set("include_children", "true");
  const query = params.toString();
  return query === "" ? "" : `?${query}`;
}

export function getWorkStatusRegistry(
  unitId?: number | null,
  includeChildren = false,
): Promise<WorkStatusRegistry> {
  const qs = sceneQuery(unitId, includeChildren);
  return api.get<WorkStatusRegistry>(`/work-status/registry${qs}`);
}

/** Пара, адресующая работу: у работы без overlay id ещё не существует. */
export interface WorkPair {
  project_id: number;
  work_type_id: number;
}

/**
 * Деталь работы. `unitId` обязателен для admin — у него нет acting-поверхности,
 * и сервер без юнита отвечает 422 (та же причина, что у реестра). Лид/зам его
 * не передаёт: сцена выводится из его поддерева.
 */
export function getWorkDetail(
  pair: WorkPair,
  unitId?: number | null,
  includeChildren = false,
): Promise<WorkStatusDetail> {
  const unit = sceneQuery(unitId, includeChildren);
  const separator = unit === "" ? "" : "&";
  return api.get<WorkStatusDetail>(
    `/work-status/works?project_id=${pair.project_id}&work_type_id=${pair.work_type_id}${
      unit === "" ? "" : `${separator}${unit.slice(1)}`
    }`,
  );
}

/**
 * Атомарный патч работы: любое подмножество полей. Сервер применяет только
 * реально изменившиеся значения, поэтому повторная отправка того же статуса или
 * той же даты закрытия — no-op без записи в журнал (§1.3). Спецобработки
 * ретраев на фронте не нужно.
 */
export interface WorkPatchBody {
  status?: ProjectWorkStatus;
  forecast?: WorkForecast;
  /** Снять прогноз. Отдельный флаг: `forecast: null` неотличим от «не прислали». */
  forecast_cleared?: boolean;
  completed_at?: string;
  /** Срок — день, к которому работа должна быть готова (ISO). */
  due_date?: string;
  /** Снять срок; по той же причине, что и `forecast_cleared`. */
  due_date_cleared?: boolean;
  memo?: string;
}

export function patchWork(pair: WorkPair, body: WorkPatchBody): Promise<WorkStatusDetail> {
  return api.post<WorkStatusDetail>("/work-status/works", { ...pair, ...body });
}

export function addCheckpoint(workId: number, title: string): Promise<WorkStatusDetail> {
  return api.post<WorkStatusDetail>(`/work-status/works/${workId}/checklist`, { title });
}

/**
 * Своя точка на работе, которой руководитель ещё не касался: работа адресуется
 * парой, потому что overlay ещё не создан. Сервер заводит её и добавляет точку
 * одной транзакцией — клиенту не нужно оркестрировать «создать → добавить».
 */
export function addCheckpointByPair(pair: WorkPair, title: string): Promise<WorkStatusDetail> {
  return api.post<WorkStatusDetail>("/work-status/works/checkpoint", { ...pair, title });
}

export function toggleCheckpoint(itemId: number, isDone: boolean): Promise<WorkStatusDetail> {
  return api.patch<WorkStatusDetail>(`/work-status/checklist/${itemId}`, { is_done: isDone });
}

/**
 * Отметка точки **каталога** — у превью нет собственного id, поэтому адресуем
 * парой + пунктом каталога. Сервер заводит работу, инстанцирует весь подходящий
 * список и ставит отметку одной транзакцией: клик по галке у нетронутой работы
 * обязан срабатывать с первого раза (решение владельца 20.07.2026).
 */
export function toggleCatalogCheckpoint(
  pair: WorkPair,
  catalogItemId: number,
  isDone: boolean,
): Promise<WorkStatusDetail> {
  return api.patch<WorkStatusDetail>("/work-status/works/checkpoint", {
    ...pair,
    catalog_item_id: catalogItemId,
    is_done: isDone,
  });
}

export function deleteCheckpoint(itemId: number): Promise<WorkStatusDetail> {
  return api.delete<WorkStatusDetail>(`/work-status/checklist/${itemId}`);
}

export function addWorkComment(workId: number, memo: string): Promise<WorkStatusDetail> {
  return api.post<WorkStatusDetail>(`/work-status/works/${workId}/comment`, { memo });
}
